import React, { useState } from 'react';
import { 
  DocumentTextIcon, 
  DocumentArrowDownIcon, 
  ShieldCheckIcon, 
  UserGroupIcon, 
  CurrencyRupeeIcon,
  MapPinIcon,
  BuildingOfficeIcon 
} from '@heroicons/react/24/outline';
import { getCurrentFinancialYear } from '../../utils/financialYear';

const PtReport = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(getCurrentFinancialYear());
  const [selectedState, setSelectedState] = useState('all');
  const [reportType, setReportType] = useState('monthly');

  // Mock data - will be replaced with API call
  const ptData = {
    summary: {
      totalEmployees: 3,
      totalPtDeduction: 705, // 235 * 3 employees
      totalPayableAmount: 705,
      statesWithPt: 1,
      averagePtPerEmployee: 235
    },
    stateWise: [
      {
        state: 'Karnataka',
        stateCode: 'KA',
        totalEmployees: 3,
        totalPtDeduction: 705,
        payableAmount: 705,
        dueDate: '2025-10-07', // 7th of next month
        status: 'Pending',
        slabs: [
          { range: 'Up to ₹15,000', rate: '₹0' },
          { range: '₹15,001 - ₹25,000', rate: '₹208' },
          { range: '₹25,001 & above', rate: '₹235' }
        ]
      }
    ],
    employees: [
      {
        employeeId: 'EMP001',
        employeeName: 'Dorothy Sanchez',
        department: 'Sales',
        state: 'Karnataka',
        grossSalary: 45333,
        ptDeduction: 235,
        ptSlab: '₹25,001 & above',
        registrationNumber: 'KA-PT-001',
        status: 'Active'
      },
      {
        employeeId: 'EMP002',
        employeeName: 'Maria Martinez',
        department: 'Design',
        state: 'Karnataka',
        grossSalary: 45333,
        ptDeduction: 235,
        ptSlab: '₹25,001 & above',
        registrationNumber: 'KA-PT-002',
        status: 'Active'
      },
      {
        employeeId: 'EMP003',
        employeeName: 'Jane Moore',
        department: 'Quality Assurance',
        state: 'Karnataka',
        grossSalary: 45334,
        ptDeduction: 235,
        ptSlab: '₹25,001 & above',
        registrationNumber: 'KA-PT-003',
        status: 'Active'
      }
    ]
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const states = [
    'All States', 'Karnataka', 'Maharashtra', 'Tamil Nadu', 'Gujarat', 
    'Andhra Pradesh', 'Telangana', 'Kerala', 'Delhi', 'West Bengal'
  ];

  const handleDownloadChallan = (state) => {
    // Generate PT Challan for specific state
    const stateData = ptData.stateWise.find(s => s.state === state);
    if (!stateData) return;

    const challanData = [
      ['Professional Tax Challan'],
      ['State:', stateData.state],
      ['State Code:', stateData.stateCode],
      ['Month/Year:', `${months[selectedMonth - 1]} ${selectedYear}`],
      ['Total Employees:', stateData.totalEmployees],
      ['Total PT Deduction:', formatCurrency(stateData.totalPtDeduction)],
      ['Due Date:', stateData.dueDate],
      [''],
      ['Employee Details:'],
      ['Employee ID', 'Employee Name', 'Gross Salary', 'PT Deduction', 'PT Slab'],
      ...ptData.employees
        .filter(emp => emp.state === state)
        .map(emp => [emp.employeeId, emp.employeeName, emp.grossSalary, emp.ptDeduction, emp.ptSlab])
    ];

    const csvContent = challanData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PT_Challan_${state}_${selectedMonth}_${selectedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadReport = () => {
    // Generate PT report CSV
    const reportData = ptData.employees.map(emp => ({
      'Employee ID': emp.employeeId,
      'Employee Name': emp.employeeName,
      'Department': emp.department,
      'State': emp.state,
      'Registration Number': emp.registrationNumber,
      'Gross Salary': emp.grossSalary,
      'PT Slab': emp.ptSlab,
      'PT Deduction': emp.ptDeduction,
      'Status': emp.status
    }));

    const headers = Object.keys(reportData[0]);
    const csvContent = [
      headers.join(','),
      ...reportData.map(row => headers.map(header => row[header]).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PT_Report_${selectedMonth}_${selectedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="w-full lg:w-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-2">
            PT Report
          </h1>
          <p className="text-gray-600 text-sm sm:text-base lg:text-lg">Professional Tax deductions state-wise and payable amounts</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <button
            onClick={handleDownloadReport}
            className="flex items-center justify-center px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 text-sm sm:text-base"
          >
            <DocumentTextIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            Download Report
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Total Employees</h3>
              <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {ptData.summary.totalEmployees}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl ml-3">
              <UserGroupIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Total PT Deduction</h3>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                {formatCurrency(ptData.summary.totalPtDeduction)}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl ml-3">
              <CurrencyRupeeIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Total Payable</h3>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
                {formatCurrency(ptData.summary.totalPayableAmount)}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-purple-100 to-violet-100 rounded-2xl ml-3">
              <BuildingOfficeIcon className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">States with PT</h3>
              <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                {ptData.summary.statesWithPt}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl ml-3">
              <MapPinIcon className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl mb-6">
        <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-4">Report Filters</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:bg-white/70"
            >
              <option value="monthly">Monthly Report</option>
              <option value="annual">Annual Report</option>
              <option value="quarterly">Quarterly Report</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full px-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:bg-white/70"
            >
              {states.map((state, index) => (
                <option key={index} value={state.toLowerCase().replace(' ', '-')}>{state}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:bg-white/70 disabled:opacity-50"
              disabled={reportType === 'annual'}
            >
              {months.map((month, index) => (
                <option key={index} value={index + 1}>{month}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:bg-white/70"
            >
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Financial Year</label>
            <select
              value={selectedFinancialYear}
              onChange={(e) => setSelectedFinancialYear(e.target.value)}
              className="w-full px-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:bg-white/70 disabled:opacity-50"
              disabled={reportType === 'monthly'}
            >
              <option value="2025-26">2025-26</option>
              <option value="2024-25">2024-25</option>
              <option value="2023-24">2023-24</option>
            </select>
          </div>
        </div>
      </div>

      {/* State-wise PT Summary */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl mb-6">
        <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-6">State-wise PT Summary</h3>

        <div className="space-y-4">
          {ptData.stateWise.map((stateInfo) => (
            <div key={stateInfo.state} className="bg-white/60 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/30 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-4 gap-4">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl shadow-lg">
                    <MapPinIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent">{stateInfo.state}</h4>
                    <p className="text-sm text-gray-500">State Code: {stateInfo.stateCode}</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto">
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(stateInfo.status)} shadow-sm`}>
                    {stateInfo.status}
                  </span>
                  <button
                    onClick={() => handleDownloadChallan(stateInfo.state)}
                    className="flex items-center justify-center px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 font-medium"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                    Download Challan
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/50 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{stateInfo.totalEmployees}</div>
                  <div className="text-sm text-gray-600 font-medium">Employees</div>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200/50 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="text-lg sm:text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">{formatCurrency(stateInfo.totalPtDeduction)}</div>
                  <div className="text-sm text-gray-600 font-medium">PT Deducted</div>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-200/50 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="text-lg sm:text-xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">{formatCurrency(stateInfo.payableAmount)}</div>
                  <div className="text-sm text-gray-600 font-medium">Payable Amount</div>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-200/50 shadow-sm hover:shadow-md transition-all duration-200">
                  <div className="text-sm font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">{stateInfo.dueDate}</div>
                  <div className="text-sm text-gray-600 font-medium">Due Date</div>
                </div>
              </div>

              {/* PT Slabs for this state */}
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-3">PT Slabs for {stateInfo.state}</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {stateInfo.slabs.map((slab, index) => (
                    <div key={index} className="text-xs bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-xl border border-blue-200/50 text-blue-800 shadow-sm hover:shadow-md transition-all duration-200">
                      <span className="font-semibold">{slab.range}:</span> <span className="font-medium">{slab.rate}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Employee-wise PT Details */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl mb-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-6 gap-4">
          <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent">Employee-wise PT Deductions</h3>
          <div className="text-sm text-gray-500 bg-white/50 px-3 py-1 rounded-full">
            {months[selectedMonth - 1]} {selectedYear}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200/50">
            <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <tr>
                <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent uppercase tracking-wider">
                  State / Registration
                </th>
                <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent uppercase tracking-wider">
                  Gross Salary
                </th>
                <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent uppercase tracking-wider">
                  PT Slab
                </th>
                <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent uppercase tracking-wider">
                  PT Deduction
                </th>
                <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white/50 divide-y divide-gray-200/50">
              {ptData.employees.map((employee) => (
                <tr key={employee.employeeId} className="hover:bg-white/80 transition-all duration-200 hover:shadow-sm">
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent">
                        {employee.employeeName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {employee.employeeId} • {employee.department}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent">{employee.state}</div>
                    <div className="text-sm text-gray-500">{employee.registrationNumber}</div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent font-medium">
                    {formatCurrency(employee.grossSalary)}
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600 bg-white/50 px-2 py-1 rounded-lg">
                    {employee.ptSlab}
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    {formatCurrency(employee.ptDeduction)}
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 shadow-sm">
                      {employee.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Summary Row */}
        <div className="mt-6 pt-4 border-t border-gray-200/50 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 rounded-xl p-4">
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 text-sm font-medium">
            <div className="sm:col-span-4 text-center sm:text-right bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent">Total PT Deduction:</div>
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent font-bold text-lg">
              {formatCurrency(ptData.summary.totalPtDeduction)}
            </div>
            <div></div>
          </div>
        </div>
      </div>

      {/* PT Compliance Information */}
      <div className="bg-gradient-to-r from-purple-50/70 to-violet-50/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-purple-200/50 shadow-xl">
        <h3 className="text-lg font-semibold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent mb-4">PT Compliance Guidelines</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-white/30 shadow-sm hover:shadow-md transition-all duration-200">
            <h4 className="text-sm font-semibold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent mb-3">Payment Deadlines</h4>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-gradient-to-r from-purple-400 to-violet-400 rounded-full"></div>
                Monthly PT: 7th of following month
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-gradient-to-r from-purple-400 to-violet-400 rounded-full"></div>
                Annual return filing as per state rules
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-gradient-to-r from-purple-400 to-violet-400 rounded-full"></div>
                Late payment attracts penalties and interest
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-gradient-to-r from-purple-400 to-violet-400 rounded-full"></div>
                Maintain proper challan records
              </li>
            </ul>
          </div>
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-white/30 shadow-sm hover:shadow-md transition-all duration-200">
            <h4 className="text-sm font-semibold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent mb-3">Important Notes</h4>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-gradient-to-r from-purple-400 to-violet-400 rounded-full"></div>
                PT rates vary by state and salary slabs
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-gradient-to-r from-purple-400 to-violet-400 rounded-full"></div>
                Some states have different rates for employees vs employers
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-gradient-to-r from-purple-400 to-violet-400 rounded-full"></div>
                Registration required in each applicable state
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-gradient-to-r from-purple-400 to-violet-400 rounded-full"></div>
                Maintain state-wise employee records
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PtReport;